import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { subscribeAcademy } from '../Admin/academyDb';
import { academyLogin, saveProgress, flattenProgress, updateProfile } from '../Admin/academyMembersData';
import { preloadImages, collectAcademyImageUrls } from './utils/preload-images';

/**
 * Academy context.
 *
 * CONTENT is loaded live from the Realtime Database (managed via the Team
 * Dashboard CMS). There is intentionally NO fallback to static data — if the
 * database is empty, the Academy shows an empty state.
 *
 * ACCESS is gated by a per-member secret key (no email/password). The key is
 * stored in localStorage and validated against the backend; each member has
 * their own progress vault.
 *
 * PROGRESS is persisted per member in the Realtime Database (via Cloud
 * Functions). It is mirrored into local state for instant UI feedback.
 *
 * In-memory progress shape (kept flat for all consumers):
 *   { [lessonKey]: { watched, quizDone, practiceDone } }
 *   where lessonKey = `${topicId}/${courseId}/${lessonId}`
 */

const KEY_STORAGE = 'ac-member-key';

const AcademyContext = createContext(null);

const lessonKey = (topicId, courseId, lessonId) => `${topicId}/${courseId}/${lessonId}`;

const loadStoredKey = () => {
  try {
    return localStorage.getItem(KEY_STORAGE) || '';
  } catch {
    return '';
  }
};

// Presentational placeholders for missing media (NOT a content fallback): keeps
// cards/players from rendering broken images until real covers/posters are set.
const demoImages = ['/img/demo__1.jpg', '/img/demo__2.jpg', '/img/demo__3.jpg', '/img/demo__4.jpg'];

const fillMedia = (topics) => {
  let cursor = 0;
  return topics.map((topic) => {
    const courses = (topic.courses || []).map((course) => {
      const lessons = (course.lessons || []).map((lesson) => {
        const preview = lesson.preview || demoImages[cursor % demoImages.length];
        cursor += 1;
        return { ...lesson, preview };
      });
      const cover = course.cover || (lessons[0] && lessons[0].preview) || demoImages[0];
      return { ...course, lessons, cover };
    });
    return { ...topic, courses };
  });
};

const DEFAULT_TIER = { id: 'none', name: '—', requiredTracks: 0, blurb: '' };

export const AcademyProvider = ({ children }) => {
  const [progress, setProgress] = useState({});

  // ── Member access (secret-key gated) ──
  const [member, setMember] = useState(null);
  const [memberLoading, setMemberLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const keyRef = useRef(loadStoredKey());

  // ── Live content from RTDB ──
  const [academy, setAcademy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = subscribeAcademy((data) => {
      setAcademy(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  const topicsAll = useMemo(() => fillMedia(academy?.topics || []), [academy]);

  // Warm the image cache once content is available so covers/posters appear
  // instantly (no pop-in) when navigating back to a page that shows them.
  useEffect(() => {
    preloadImages(collectAcademyImageUrls(topicsAll));
  }, [topicsAll]);

  // Only show courses this member has been granted access to. Topics with no
  // accessible course drop out entirely (locked courses are hidden, not shown).
  const topics = useMemo(() => {
    const access = member?.access || {};
    return topicsAll
      .map((t) => ({
        ...t,
        courses: (t.courses || []).filter((c) => access[`${t.id}__${c.id}`]),
      }))
      .filter((t) => t.courses.length > 0);
  }, [topicsAll, member]);

  const tiers = useMemo(
    () => (Array.isArray(academy?.tiers) && academy.tiers.length ? academy.tiers : []),
    [academy]
  );
  const badges = academy?.badges || {};

  // ── Content selectors ──
  const getTopic = useCallback((topicId) => topics.find((t) => t.id === topicId) || null, [topics]);
  const getCourse = useCallback(
    (topicId, courseId) => {
      const topic = topics.find((t) => t.id === topicId);
      return topic ? topic.courses.find((c) => c.id === courseId) || null : null;
    },
    [topics]
  );
  const getLesson = useCallback(
    (topicId, courseId, lessonId) => {
      const course = getCourse(topicId, courseId);
      return course ? course.lessons.find((l) => l.id === lessonId) || null : null;
    },
    [getCourse]
  );
  const getTopicLessonCount = useCallback(
    (topic) => (topic?.courses || []).reduce((acc, c) => acc + (c.lessons || []).length, 0),
    []
  );
  const getBadgeDesign = useCallback(
    (badgeId) => {
      const design = badges[badgeId] || {};
      return { color: design.color || '#6da7ed', description: design.description || '' };
    },
    [badges]
  );

  // Validate a stored key on mount so a returning member stays logged in.
  useEffect(() => {
    let cancelled = false;
    const storedKey = keyRef.current;
    if (!storedKey) {
      setMemberLoading(false);
      return undefined;
    }
    (async () => {
      try {
        const { member: m, progress: p } = await academyLogin(storedKey);
        if (cancelled) return;
        setMember(m);
        setProgress(flattenProgress(p));
      } catch (e) {
        if (cancelled) return;
        keyRef.current = '';
        try {
          localStorage.removeItem(KEY_STORAGE);
        } catch {
          /* ignore */
        }
        setMember(null);
      } finally {
        if (!cancelled) setMemberLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Exchange a secret key for a member session. Throws on invalid key.
  const login = useCallback(async (rawKey) => {
    const key = (rawKey || '').trim();
    setAuthError('');
    if (!key) {
      setAuthError('Please enter your access key.');
      return false;
    }
    try {
      const { member: m, progress: p } = await academyLogin(key);
      keyRef.current = key;
      try {
        localStorage.setItem(KEY_STORAGE, key);
      } catch {
        /* ignore */
      }
      setMember(m);
      setProgress(flattenProgress(p));
      return true;
    } catch (e) {
      setAuthError(
        e.status === 403 ? 'This access key has been disabled.' : 'Invalid access key. Please check and try again.'
      );
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    keyRef.current = '';
    try {
      localStorage.removeItem(KEY_STORAGE);
    } catch {
      /* ignore */
    }
    setMember(null);
    setProgress({});
  }, []);

  // First-login onboarding: a member must have BOTH a name and an email before
  // entering. Team Dashboard contacts are often created with just a name (or
  // just a key) and no email — in that case we ask the member to complete their
  // details on login (and optionally add a WhatsApp number).
  const needsProfile =
    !!member && (!((member.name || '').trim()) || !((member.email || '').trim()));

  const completeProfile = useCallback(async ({ name, email, whatsapp }) => {
    const key = keyRef.current;
    if (!key) return false;
    const updated = await updateProfile({ key, name, email, whatsapp });
    setMember((prev) => ({ ...(prev || {}), ...updated }));
    return true;
  }, []);

  // Persist a single completed step to the member vault (fire-and-forget; the
  // local optimistic update already drives the UI). `submission` is optional and
  // stores the actual content the member entered (practice text / quiz answers).
  const persist = useCallback((topicId, courseId, lessonId, kind, submission) => {
    const key = keyRef.current;
    if (!key) return;
    saveProgress({ key, topicId, courseId, lessonId, kind, submission }).catch(() => {
      /* offline / transient — local state stays, retried on next mark/login */
    });
  }, []);

  const markWatched = useCallback(
    (topicId, courseId, lessonId) => {
      const k = lessonKey(topicId, courseId, lessonId);
      setProgress((prev) => ({ ...prev, [k]: { ...prev[k], watched: true } }));
      persist(topicId, courseId, lessonId, 'watched');
    },
    [persist]
  );

  const markQuizDone = useCallback(
    (topicId, courseId, lessonId, submission) => {
      const k = lessonKey(topicId, courseId, lessonId);
      setProgress((prev) => ({ ...prev, [k]: { ...prev[k], quizDone: true, submission: submission || prev[k]?.submission } }));
      persist(topicId, courseId, lessonId, 'quizDone', submission);
    },
    [persist]
  );

  const markPracticeDone = useCallback(
    (topicId, courseId, lessonId, submission) => {
      const k = lessonKey(topicId, courseId, lessonId);
      setProgress((prev) => ({ ...prev, [k]: { ...prev[k], practiceDone: true, submission: submission || prev[k]?.submission } }));
      persist(topicId, courseId, lessonId, 'practiceDone', submission);
    },
    [persist]
  );

  const resetProgress = useCallback(() => setProgress({}), []);

  // Each lesson is exactly one step of one type (video / practice / quiz).
  // For legacy data without an explicit type, infer it from the content.
  const lessonType = useCallback((lesson) => {
    if (lesson?.type === 'video' || lesson?.type === 'practice' || lesson?.type === 'quiz') return lesson.type;
    if (lesson?.practiceLab) return 'practice';
    if (lesson?.quiz) return 'quiz';
    return 'video';
  }, []);

  // A lesson is done when its single step is completed (watched / practice / quiz).
  const isLessonDone = useCallback(
    (topicId, courseId, lesson) => {
      const entry = progress[lessonKey(topicId, courseId, lesson.id)] || {};
      const t = lessonType(lesson);
      if (t === 'practice') return !!entry.practiceDone;
      if (t === 'quiz') return !!entry.quizDone;
      return !!entry.watched;
    },
    [progress, lessonType]
  );

  const getLessonState = useCallback(
    (topicId, courseId, lessonId) => progress[lessonKey(topicId, courseId, lessonId)] || {},
    [progress]
  );

  // Build the ordered list of path steps for a course — exactly one step per
  // lesson (its type) — with completion + progressive-unlock state.
  //
  // Unlock rule: a step is unlocked only when every previous step is done.
  // → effectively only the first not-yet-completed step is "active"; watched
  //   videos stay accessible (for review), everything after the current step
  //   is locked.
  const getCourseSteps = useCallback(
    (topicId, course) => {
      const steps = [];
      course.lessons.forEach((lesson) => {
        const state = getLessonState(topicId, course.id, lesson.id);
        const t = lessonType(lesson);
        if (t === 'practice') {
          steps.push({ type: 'practice', lesson, key: `${lesson.id}-p`, done: !!state.practiceDone });
        } else if (t === 'quiz') {
          steps.push({ type: 'quiz', lesson, key: `${lesson.id}-q`, done: !!state.quizDone });
        } else {
          steps.push({ type: 'video', lesson, key: `${lesson.id}-v`, done: !!state.watched });
        }
      });

      let prevAllDone = true;
      steps.forEach((step) => {
        step.locked = !prevAllDone;
        step.active = prevAllDone && !step.done;
        prevAllDone = prevAllDone && step.done;
      });
      return steps;
    },
    [getLessonState, lessonType]
  );

  // Course progress: how many lessons are done out of total.
  const getCourseProgress = useCallback(
    (topicId, course) => {
      const total = course.lessons.length;
      const done = course.lessons.filter((l) => isLessonDone(topicId, course.id, l)).length;
      return { done, total, isComplete: total > 0 && done === total };
    },
    [isLessonDone]
  );

  // Totals derived from the live content tree.
  const totalCourseCount = useMemo(
    () => topics.reduce((acc, t) => acc + (t.courses || []).length, 0),
    [topics]
  );
  const totalTrackCount = topics.length;

  // Count of fully completed courses across all topics.
  const completedCourseCount = useMemo(() => {
    let count = 0;
    topics.forEach((topic) => {
      topic.courses.forEach((course) => {
        if (getCourseProgress(topic.id, course).isComplete) count += 1;
      });
    });
    return count;
  }, [topics, getCourseProgress]);

  // Count of fully completed study tracks (topics) → drives the tier system.
  const completedTrackCount = useMemo(() => {
    let count = 0;
    topics.forEach((topic) => {
      const allDone =
        topic.courses.length > 0 &&
        topic.courses.every((course) => getCourseProgress(topic.id, course).isComplete);
      if (allDone) count += 1;
    });
    return count;
  }, [topics, getCourseProgress]);

  // ── Badges (per completed course) ──
  const getAllBadges = useCallback(() => {
    const list = [];
    topics.forEach((topic) => {
      topic.courses.forEach((course) => {
        if (!course.badge) return;
        const design = getBadgeDesign(course.badge.id);
        list.push({
          id: course.badge.id,
          name: course.badge.name,
          color: design.color || topic.accent,
          description: design.description || '',
          topicId: topic.id,
          courseId: course.id,
          courseTitle: course.title,
          earned: getCourseProgress(topic.id, course).isComplete,
        });
      });
    });
    return list;
  }, [topics, getBadgeDesign, getCourseProgress]);

  const getEarnedBadges = useCallback(
    () => getAllBadges().filter((b) => b.earned),
    [getAllBadges]
  );

  // Current tier = highest tier whose requirement is met.
  const currentTier = useMemo(() => {
    const eligible = tiers.filter((t) => completedTrackCount >= t.requiredTracks);
    return eligible[eligible.length - 1] || tiers[0] || DEFAULT_TIER;
  }, [tiers, completedTrackCount]);

  // Next tier (the first one not yet unlocked) + progress toward it.
  const nextTier = useMemo(
    () => tiers.find((t) => completedTrackCount < t.requiredTracks) || null,
    [tiers, completedTrackCount]
  );

  const tierProgress = useMemo(() => {
    if (!nextTier) return { toNext: 0, pct: 100 };
    const from = currentTier.requiredTracks;
    const to = nextTier.requiredTracks;
    const span = Math.max(1, to - from);
    const within = Math.min(span, Math.max(0, completedTrackCount - from));
    return {
      toNext: Math.max(0, to - completedTrackCount),
      pct: Math.round((within / span) * 100),
    };
  }, [currentTier, nextTier, completedTrackCount]);

  const value = useMemo(
    () => ({
      // member access
      member,
      memberLoading,
      authError,
      login,
      logout,
      needsProfile,
      completeProfile,
      // content
      loading,
      isEmpty: !loading && topics.length === 0,
      topics,
      tiers,
      getTopic,
      getCourse,
      getLesson,
      getTopicLessonCount,
      getBadgeDesign,
      // progress
      progress,
      markWatched,
      markQuizDone,
      markPracticeDone,
      resetProgress,
      isLessonDone,
      lessonType,
      getLessonState,
      getCourseProgress,
      getCourseSteps,
      getAllBadges,
      getEarnedBadges,
      completedCourseCount,
      completedTrackCount,
      totalCourseCount,
      totalTrackCount,
      currentTier,
      nextTier,
      tierProgress,
    }),
    [
      member,
      memberLoading,
      authError,
      login,
      logout,
      needsProfile,
      completeProfile,
      loading,
      topics,
      tiers,
      getTopic,
      getCourse,
      getLesson,
      getTopicLessonCount,
      getBadgeDesign,
      progress,
      markWatched,
      markQuizDone,
      markPracticeDone,
      resetProgress,
      isLessonDone,
      lessonType,
      getLessonState,
      getCourseProgress,
      getCourseSteps,
      getAllBadges,
      getEarnedBadges,
      completedCourseCount,
      completedTrackCount,
      totalCourseCount,
      totalTrackCount,
      currentTier,
      nextTier,
      tierProgress,
    ]
  );

  return <AcademyContext.Provider value={value}>{children}</AcademyContext.Provider>;
};

export const useAcademy = () => {
  const ctx = useContext(AcademyContext);
  if (!ctx) throw new Error('useAcademy must be used within an AcademyProvider');
  return ctx;
};
